import React, { useEffect, useState } from "react";
import { ReactComponent as FourStar } from "../../../../assets/Affiliate/superStart.svg";
import { SUPER_AFFILIATE_STATUS } from "../../../../store/reducers/affiliateReducer";
import ApplicationModal from "./ApplicationModal";
import PromoCodeSetupModal, { PromoCodeList } from "./PromoCodeSetup";
import "./SuperAffiliate.scss";

const normalise = (status) => String(status || "").trim().toLowerCase();
const UNDER_REVIEW = normalise(SUPER_AFFILIATE_STATUS.UNDER_REVIEW);
const APPROVED = normalise(SUPER_AFFILIATE_STATUS.APPROVED);
const REJECTED = normalise(SUPER_AFFILIATE_STATUS.REJECTED);

const formatDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? String(value)
    : date.toLocaleDateString("en-GB", {
        year: "numeric",
        month: "short",
        day: "2-digit",
      });
};

const SuperAffiliateSection = ({
  superAffiliate,
  isSuperAffiliate,
  onRefresh,
}) => {
  const [applyOpen, setApplyOpen] = useState(false);
  const [promoOpen, setPromoOpen] = useState(false);
  // Set on submit so the status flips without waiting for the dashboard refetch.
  const [justApplied, setJustApplied] = useState(false);
  // Remembers a manual dismissal so the pop-up does not fight the user.
  const [promoDismissed, setPromoDismissed] = useState(false);

  const status = normalise(superAffiliate?.application_status);
  const approved = Boolean(isSuperAffiliate) || status === APPROVED;
  const underReview = !approved && (justApplied || status === UNDER_REVIEW);
  const rejected = !approved && !underReview && status === REJECTED;
  const canApply = Boolean(superAffiliate?.can_apply) && !underReview;

  // Only affiliates approved through the application flow get prompted; legacy
  // Super Affiliates keep their admin-created codes.
  const setupRequired =
    approved &&
    Boolean(superAffiliate?.can_setup_promo_codes) &&
    Boolean(superAffiliate?.promo_setup_required);

  // The pop-up shows while promo_setup_required is true. Claiming both codes
  // flips it false on the next dashboard fetch, which closes this for good.
  useEffect(() => {
    if (setupRequired && !promoDismissed) setPromoOpen(true);
    if (!setupRequired) setPromoOpen(false);
  }, [setupRequired, promoDismissed]);

  const statusLine = () => {
    if (approved) return "You are a Super Affiliate.";
    if (underReview) return "Submission sent. Status: Under Review";
    if (rejected) {
      const reapplyOn = formatDate(superAffiliate?.can_reapply_on);
      const cooldown = superAffiliate?.reapply_cooldown_days;
      if (reapplyOn)
        return `Status: Rejected. You can apply again from ${reapplyOn}.`;
      return cooldown
        ? `Status: Rejected. You can apply again after ${cooldown} days.`
        : "Status: Rejected.";
    }
    return "Apply to unlock Super Affiliate commission and promo codes.";
  };

  const statusClass = approved
    ? "approved"
    : underReview
    ? "review"
    : rejected
    ? "rejected"
    : "";

  return (
    <div className="super-affiliate">
      <div className={`sa-card ${approved ? "active" : ""}`}>
        <div className="sa-card-star">
          <FourStar />
        </div>
        <p className="sa-card-title">Super Affiliate</p>
      </div>

      {!approved && (
        <button
          type="button"
          className="sa-apply-btn"
          onClick={() => setApplyOpen(true)}
          disabled={!canApply}
          aria-disabled={!canApply}
        >
          {underReview ? "Application Submitted" : "Apply Here"}
        </button>
      )}

      <p className={`sa-status ${statusClass}`}>{statusLine()}</p>

      {/* Reopen after a dismissal, until both codes are claimed. */}
      {setupRequired && !promoOpen && (
        <button
          type="button"
          className="sa-apply-btn"
          onClick={() => {
            setPromoDismissed(false);
            setPromoOpen(true);
          }}
        >
          Set Up Promo Codes
        </button>
      )}

      {approved && <PromoCodeList superAffiliate={superAffiliate} />}

      <ApplicationModal
        open={applyOpen}
        onClose={() => setApplyOpen(false)}
        onSubmitted={() => {
          setJustApplied(true);
          onRefresh?.();
        }}
      />

      <PromoCodeSetupModal
        open={promoOpen}
        superAffiliate={superAffiliate}
        onClose={() => {
          setPromoDismissed(true);
          setPromoOpen(false);
        }}
        onClaimed={onRefresh}
      />
    </div>
  );
};

export default SuperAffiliateSection;
